"use client"

import * as React from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FlameIcon,
  type LucideIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  SmileIcon,
  SparklesIcon,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { type Deroule, JOURS, type Statut } from "@/lib/data/types"
import { enregistrerReponse } from "@/app/mon-espace/actions"

/**
 * Le temps de repos glissé entre deux étapes quand on enchaîne — une respiration,
 * pas une minuterie de salle de sport. Court et constant : assez pour reprendre
 * son souffle, jamais assez pour perdre le fil. On peut toujours le passer.
 */
const REPOS_SECONDES = 15

/**
 * La séance vécue — le concept qui sépare Cairn d'une liste sur papier.
 *
 * Là où le dashboard *liste* ce qui est prévu et le « comment faire » se lit
 * replié, cet écran fait *traverser* la séance : un moment à la fois, en grand,
 * avec un minuteur sobre pour ce qui se tient dans le temps. On termine sur un
 * geste qui boucle la boucle — noter le créneau comme fait, sans repasser par la
 * grille. Plein écran, focalisé : rien d'autre à l'esprit que l'instant présent.
 */
export function SeanceVecue({
  clientId,
  tacheId,
  label,
  coach,
  deroule,
  jour,
  statutInitial,
}: {
  clientId: string
  tacheId: string
  label: string
  /** Le prénom du coach : sa voix referme la séance, elle ne se coche pas seule. */
  coach: string
  deroule: Deroule
  /** Le créneau d'où l'on vient, s'il est valide — ce qu'on pourra noter à la fin. */
  jour: number | null
  statutInitial: Statut | null
}) {
  const total = deroule.etapes.length
  /* De 0 à total-1 sur les étapes ; `total` est l'écran de fin. */
  const [index, setIndex] = React.useState(0)
  /* Deux temps possibles sur une même étape : la vivre (`etape`) ou souffler
     avant la suivante (`repos`), quand on enchaîne. */
  const [phase, setPhase] = React.useState<"etape" | "repos">("etape")
  /* L'enchaînement guidé : les étapes minutées démarrent seules et coulent l'une
     dans l'autre, un repos entre deux. Éteint par défaut — on ne presse personne :
     c'est le client qui choisit d'être porté. Sans étape minutée, rien à enchaîner. */
  const aDesMinuteurs = deroule.etapes.some((e) => e.duree)
  const [enchaine, setEnchaine] = React.useState(false)
  const [annonce, setAnnonce] = React.useState("")
  const fin = index >= total

  function annoncer(i: number) {
    if (i >= total) {
      setAnnonce("Séance terminée.")
    } else {
      setAnnonce(`Étape ${i + 1} sur ${total} : ${deroule.etapes[i].titre}.`)
    }
  }

  /* Toute navigation d'étape ramène au temps « vivre » : on ne reste jamais en
     repos après un saut manuel. */
  function aller(vers: number) {
    const cible = Math.max(0, Math.min(total, vers))
    setIndex(cible)
    setPhase("etape")
    annoncer(cible)
  }

  /* Fin d'une étape minutée : ne fait quelque chose que si l'on enchaîne. Sinon
     le minuteur s'arrête et attend, comme avant. S'il reste une étape, on passe
     par un repos ; si c'était la dernière, on file à l'écran de fin. */
  function surFinMinuteur() {
    if (!enchaine || phase !== "etape") return
    if (index < total - 1) {
      setPhase("repos")
      setAnnonce(
        `Repos. Prochaine étape : ${deroule.etapes[index + 1].titre}.`
      )
    } else {
      aller(total)
    }
  }

  return (
    <div className="flex min-h-[100dvh] flex-col pb-10">
      <p role="status" aria-live="polite" className="sr-only">
        {annonce}
      </p>

      {/* -------------------------------------------------------- en-tête */}
      <header className="mx-auto flex w-full max-w-[560px] items-center justify-between px-gutter pt-8">
        <Link
          href="/mon-espace"
          className="flex items-center gap-2 rounded-sm text-faint outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <ArrowLeftIcon aria-hidden className="size-4" />
          <span className="font-heading text-xs tracking-brand uppercase">
            Quitter
          </span>
        </Link>
        <span className="max-w-[60%] truncate font-heading text-xs tracking-brand text-faint uppercase">
          {label}
        </span>
      </header>

      {/* L'interrupteur d'enchaînement — n'a de sens que tant qu'on traverse, et
          seulement si une étape se tient dans le temps. */}
      {aDesMinuteurs && !fin ? (
        <div className="mx-auto flex w-full max-w-[560px] justify-end px-gutter pt-4">
          <InterrupteurEnchaine
            actif={enchaine}
            onChange={() => setEnchaine((e) => !e)}
          />
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col px-gutter">
        {fin ? (
          <FinSeance
            clientId={clientId}
            tacheId={tacheId}
            coach={coach}
            jour={jour}
            dejaFait={statutInitial === "fait"}
          />
        ) : phase === "repos" ? (
          <Repos
            key={`repos-${index}`}
            prochaine={deroule.etapes[index + 1].titre}
            onFini={() => aller(index + 1)}
          />
        ) : (
          <Etape
            deroule={deroule}
            index={index}
            total={total}
            enchaine={enchaine}
            onMinuteurFini={surFinMinuteur}
            onPrecedent={() => aller(index - 1)}
            onSuivant={() => aller(index + 1)}
          />
        )}
      </div>
    </div>
  )
}

/**
 * L'interrupteur d'enchaînement guidé — sobre, dans la palette.
 *
 * Un vrai `switch` accessible : le client choisit d'être porté d'une étape à
 * l'autre, ou de rester maître du tempo. Jamais coché d'office : l'app accompagne
 * quand on le lui demande, elle ne pousse pas.
 */
function InterrupteurEnchaine({
  actif,
  onChange,
}: {
  actif: boolean
  onChange: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={actif}
      onClick={onChange}
      className="flex items-center gap-2.5 rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <span className="font-heading text-xs tracking-brand text-faint uppercase">
        Enchaîner
      </span>
      <span
        aria-hidden
        className={cn(
          "relative h-5 w-9 rounded-full border transition-colors",
          actif ? "border-foreground bg-foreground/20" : "border-border-strong"
        )}
      >
        <span
          className={cn(
            "absolute top-1/2 size-3 -translate-y-1/2 rounded-full transition-all",
            actif
              ? "left-[calc(100%-0.875rem)] bg-foreground"
              : "left-0.5 bg-border-strong"
          )}
        />
      </span>
    </button>
  )
}

/* ------------------------------------------------------------- morceaux */

/** Le moment présent : l'étape en grand, son minuteur s'il y a lieu, les commandes. */
function Etape({
  deroule,
  index,
  total,
  enchaine,
  onMinuteurFini,
  onPrecedent,
  onSuivant,
}: {
  deroule: Deroule
  index: number
  total: number
  enchaine: boolean
  onMinuteurFini: () => void
  onPrecedent: () => void
  onSuivant: () => void
}) {
  const etape = deroule.etapes[index]
  const dernier = index === total - 1

  return (
    <div className="flex flex-1 flex-col pt-8">
      {/* Où l'on en est : un segment par étape, franchis à mesure. */}
      <div className="flex items-center gap-3">
        <div aria-hidden className="flex flex-1 gap-1.5">
          {deroule.etapes.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                i <= index ? "bg-foreground" : "bg-border"
              )}
            />
          ))}
        </div>
        <span className="font-heading text-xs tracking-brand text-faint uppercase">
          {index + 1} / {total}
        </span>
      </div>

      {/* L'étape elle-même, centrée dans l'espace qui reste. */}
      <div className="flex flex-1 flex-col justify-center py-10">
        {index === 0 && deroule.intro ? (
          <p className="mb-6 text-[0.9375rem] leading-relaxed text-faint text-pretty">
            {deroule.intro}
          </p>
        ) : null}

        <h1 className="font-heading text-[clamp(1.75rem,7vw,2.5rem)] leading-[1.05] font-medium uppercase text-pretty">
          {etape.titre}
        </h1>

        {etape.detail ? (
          <p className="mt-4 text-[1.0625rem] leading-relaxed text-muted-foreground text-pretty">
            {etape.detail}
          </p>
        ) : null}

        {etape.duree ? (
          <Minuteur
            key={index}
            duree={etape.duree}
            autoDemarrage={enchaine}
            onFini={onMinuteurFini}
          />
        ) : enchaine && !dernier ? (
          // Une étape sans durée ne s'enchaîne pas toute seule : on ne peut pas
          // deviner quand un circuit est fini. On le dit doucement plutôt que de
          // laisser croire que ça va avancer sans le client.
          <p className="mt-8 text-center font-heading text-xs tracking-brand text-faint uppercase">
            À votre rythme — « Suivant » quand vous êtes prêt·e
          </p>
        ) : null}
      </div>

      {/* -------------------------------------------------------- commandes */}
      <div className="flex items-center gap-3 py-6">
        <button
          type="button"
          onClick={onPrecedent}
          disabled={index === 0}
          className="flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-3 font-heading text-[0.8125rem] tracking-brand uppercase text-muted-foreground outline-none transition-colors hover:border-border-strong hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronLeftIcon aria-hidden className="size-4" />
          <span className="sr-only sm:not-sr-only">Précédent</span>
        </button>
        <button
          type="button"
          onClick={onSuivant}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-foreground bg-foreground px-4 py-3 font-heading text-[0.8125rem] tracking-brand uppercase text-background outline-none transition-colors hover:bg-foreground/90 focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {dernier ? "Terminer" : "Suivant"}
          <ChevronRightIcon aria-hidden className="size-4" />
        </button>
      </div>
    </div>
  )
}

/**
 * Le minuteur d'une étape tenue dans le temps — sobre, jamais pressant.
 *
 * Un anneau qui se vide, le temps qui reste au centre. Pas de son, pas de
 * clignotement : l'app accompagne, elle ne bipe pas. À zéro, elle le dit une
 * fois (« Temps écoulé »), au lecteur d'écran comme à l'œil, et laisse le client
 * repartir quand il veut — ou, si l'on enchaîne, prévient le parent d'enchaîner.
 */
function Minuteur({
  duree,
  autoDemarrage,
  onFini,
}: {
  duree: number
  /** Démarre seul quand on enchaîne — le client n'a pas à lancer chaque étape. */
  autoDemarrage?: boolean
  /** Prévenu une fois, quand le temps atteint zéro. */
  onFini?: () => void
}) {
  /* Chaque étape monte un minuteur neuf (clé = index) : l'état initial suffit à
     le démarrer seul quand on enchaîne, sans jamais forcer `actif` depuis un
     effet. Le client garde la main — pause, reprise, réinitialisation. */
  const [reste, setReste] = React.useState(duree)
  const [actif, setActif] = React.useState(autoDemarrage ?? false)

  React.useEffect(() => {
    if (!actif || reste <= 0) return
    const id = setTimeout(() => setReste((r) => r - 1), 1000)
    return () => clearTimeout(id)
  }, [actif, reste])

  /* Prévenir le parent une seule fois, à l'instant où le temps atteint zéro.
     `onFini` passe par une ref pour que son identité ne relance pas l'effet —
     seul le passage à zéro déclenche. */
  const finiRef = React.useRef(onFini)
  React.useEffect(() => {
    finiRef.current = onFini
  }, [onFini])
  React.useEffect(() => {
    if (reste <= 0) finiRef.current?.()
  }, [reste])

  /* À zéro, plus rien n'est programmé : le minuteur s'arrête de lui-même, sans
     qu'on ait à remettre `actif` à false depuis un effet. */
  const fini = reste <= 0
  const enMarche = actif && !fini
  const ratio = duree > 0 ? reste / duree : 0

  /* L'anneau : rayon 52 dans une boîte 120, contour 4. La longueur de trait
     dessinée suit le temps restant. */
  const rayon = 52
  const circonference = 2 * Math.PI * rayon

  return (
    <div className="mt-9 flex flex-col items-center">
      <div className="relative grid size-40 place-items-center">
        <svg viewBox="0 0 120 120" aria-hidden className="size-40 -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={rayon}
            fill="none"
            strokeWidth="4"
            className="stroke-border"
          />
          <circle
            cx="60"
            cy="60"
            r={rayon}
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circonference}
            strokeDashoffset={circonference * (1 - ratio)}
            className={cn(
              "transition-[stroke-dashoffset] duration-1000 ease-linear",
              fini ? "stroke-border-strong" : "stroke-foreground"
            )}
          />
        </svg>
        <span className="absolute font-heading text-[2rem] leading-none tabular-nums">
          {fini ? "0s" : formatTemps(reste)}
        </span>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setActif((a) => !a)}
          disabled={fini}
          className="flex items-center gap-2 rounded-lg border border-foreground bg-foreground px-5 py-2.5 font-heading text-[0.8125rem] tracking-brand uppercase text-background outline-none transition-colors hover:bg-foreground/90 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-40"
        >
          {enMarche ? (
            <>
              <PauseIcon aria-hidden className="size-4" />
              Pause
            </>
          ) : (
            <>
              <PlayIcon aria-hidden className="size-4" />
              Démarrer
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setActif(false)
            setReste(duree)
          }}
          aria-label="Réinitialiser le minuteur"
          className="grid size-11 place-items-center rounded-lg border border-border text-muted-foreground outline-none transition-colors hover:border-border-strong hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <RotateCcwIcon aria-hidden className="size-4" />
        </button>
      </div>

      <span role="status" aria-live="polite" className="sr-only">
        {fini ? "Temps écoulé." : ""}
      </span>
    </div>
  )
}

/**
 * Le repos entre deux étapes, quand on enchaîne — une respiration guidée.
 *
 * Ni un temps mort ni une minuterie de salle : un moment court qui annonce la
 * suite, coule tout seul vers l'étape d'après, et qu'on peut toujours écourter.
 * Sobre, dans la palette : un décompte, la prochaine étape nommée, une barre qui
 * se vide. Aucun son, aucun clignotement.
 */
function Repos({
  prochaine,
  onFini,
}: {
  prochaine: string
  onFini: () => void
}) {
  const [reste, setReste] = React.useState(REPOS_SECONDES)

  React.useEffect(() => {
    if (reste <= 0) return
    const id = setTimeout(() => setReste((r) => r - 1), 1000)
    return () => clearTimeout(id)
  }, [reste])

  /* Une ref pour ne déclencher `onFini` qu'au passage à zéro, sans que son
     identité ne relance l'effet. */
  const finiRef = React.useRef(onFini)
  React.useEffect(() => {
    finiRef.current = onFini
  }, [onFini])
  React.useEffect(() => {
    if (reste <= 0) finiRef.current()
  }, [reste])

  const ratio = REPOS_SECONDES > 0 ? reste / REPOS_SECONDES : 0

  return (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
      <p className="font-heading text-xs tracking-brand text-faint uppercase">
        Repos
      </p>
      <p className="mt-6 font-heading text-[clamp(3rem,16vw,4.5rem)] leading-none tabular-nums">
        {formatTemps(reste)}
      </p>

      <div
        aria-hidden
        className="mt-8 h-1 w-full max-w-[240px] overflow-hidden rounded-full bg-border"
      >
        <div
          className="h-1 rounded-full bg-border-strong transition-[width] duration-1000 ease-linear"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      <p className="mt-8 max-w-[36ch] text-[0.9375rem] leading-relaxed text-muted-foreground text-pretty">
        <span className="font-heading text-xs tracking-brand text-faint uppercase">
          Ensuite
        </span>
        <br />
        <span className="mt-1 inline-block font-heading text-[1.25rem] uppercase text-foreground">
          {prochaine}
        </span>
      </p>

      <button
        type="button"
        onClick={onFini}
        className="mt-9 flex items-center justify-center gap-2 rounded-lg border border-border px-5 py-3 font-heading text-[0.8125rem] tracking-brand uppercase text-muted-foreground outline-none transition-colors hover:border-border-strong hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        Passer le repos
        <ChevronRightIcon aria-hidden className="size-4" />
      </button>
    </div>
  )
}

/**
 * Les trois ressentis qu'on peut poser en fin de séance — un geste, pas un
 * questionnaire.
 *
 * Chacun appelle une réaction du coach : sa voix déposée pour l'instant où il
 * n'est pas là. C'est ce qui sépare Cairn d'une case à cocher — on ne clôt pas
 * une séance seul·e, on la referme avec quelqu'un. Tutoyé comme le mot du coach.
 */
type Ressenti = "forme" | "bien" | "exigeant"

const RESSENTIS: {
  id: Ressenti
  label: string
  Icon: LucideIcon
  reaction: string
}[] = [
  {
    id: "forme",
    label: "En forme",
    Icon: SparklesIcon,
    reaction:
      "Cette énergie, c'est le signe que le corps prend le rythme. Garde-la — on continue comme ça.",
  },
  {
    id: "bien",
    label: "Ça a fait du bien",
    Icon: SmileIcon,
    reaction:
      "C'est exactement ce qu'on cherche. Une séance de plus posée, tranquillement. Bravo.",
  },
  {
    id: "exigeant",
    label: "C'était exigeant",
    Icon: FlameIcon,
    reaction:
      "Les séances difficiles sont celles qui comptent le plus, et tu l'as menée au bout. C'est là que ça se construit.",
  },
]

/**
 * L'après-séance : le moment où la cliente sent qu'elle a été accompagnée, pas
 * qu'elle a coché.
 *
 * Traverser la séance ne doit pas s'éteindre sur un bouton sec. Deux temps :
 * d'abord elle pose comment elle se sent, d'un geste ; puis le coach lui répond
 * — sa voix, en réaction à ce qu'elle vient de vivre — et la séance rejoint son
 * parcours. Le créneau est noté « fait » dès la fin (le vrai signal, c'est
 * d'avoir traversé la séance), qu'elle réponde au ressenti ou choisisse de
 * passer. Sobre : une présence, jamais des confettis.
 */
function FinSeance({
  clientId,
  tacheId,
  coach,
  jour,
  dejaFait,
}: {
  clientId: string
  tacheId: string
  coach: string
  jour: number | null
  dejaFait: boolean
}) {
  const router = useRouter()
  const [enCours, demarrer] = React.useTransition()
  const [erreur, setErreur] = React.useState(false)
  const [ressenti, setRessenti] = React.useState<Ressenti | null>(null)
  /* La séance est-elle notée « fait » ? Vrai d'entrée si le créneau l'était
     déjà — on ne réécrit pas ce qui est acquis. */
  const [note, setNote] = React.useState(dejaFait)

  /* Noter le créneau « fait ». Optimiste, puis honnête : on marque `note` au
     succès, on lève `erreur` à l'échec — l'app ne fait jamais semblant d'avoir
     gardé ce qu'elle a perdu. `apres` enchaîne une navigation le cas échéant. */
  function noter(apres?: () => void) {
    if (jour === null || note) {
      apres?.()
      return
    }
    setErreur(false)
    demarrer(async () => {
      try {
        await enregistrerReponse(clientId, tacheId, jour, "fait")
        setNote(true)
        apres?.()
      } catch {
        setErreur(true)
      }
    })
  }

  /* Poser un ressenti clôt la séance : on note le créneau et on révèle la
     réaction du coach. */
  function choisir(r: Ressenti) {
    setRessenti(r)
    noter()
  }

  /* « Passer » : on note quand même (la séance est faite), on rentre. */
  function passer() {
    noter(() => {
      router.push("/mon-espace")
      router.refresh()
    })
  }

  /* Premier temps : le ressenti pas encore posé. */
  if (ressenti === null) {
    return (
      <div className="flex flex-1 flex-col justify-center py-12">
        <div className="flex flex-col items-center text-center">
          <span
            aria-hidden
            className="grid size-14 place-items-center rounded-full border border-border-strong bg-foreground/10 text-foreground"
          >
            <CheckIcon className="size-7" />
          </span>
          <h1 className="mt-6 font-heading text-[clamp(1.75rem,7vw,2.5rem)] leading-none font-medium uppercase">
            Séance terminée
          </h1>
          <p className="mt-3 max-w-[36ch] text-[0.9375rem] leading-relaxed text-muted-foreground text-pretty">
            Bien joué. Vous avez traversé toute la séance, à votre rythme.
          </p>
        </div>

        <div className="mt-10">
          <p className="text-center font-heading text-xs tracking-brand text-faint uppercase">
            Comment vous sentez-vous ?
          </p>
          <div className="mx-auto mt-5 grid w-full max-w-[360px] gap-3">
            {RESSENTIS.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => choisir(r.id)}
                className="group flex items-center gap-3.5 rounded-lg border border-border bg-card px-5 py-4 text-left outline-none transition-colors hover:border-border-strong focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <r.Icon
                  aria-hidden
                  className="size-5 shrink-0 text-faint transition-colors group-hover:text-foreground"
                />
                <span className="font-heading text-[0.9375rem] tracking-brand uppercase">
                  {r.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={passer}
            disabled={enCours}
            className="rounded-sm font-heading text-xs tracking-brand text-faint uppercase outline-none transition-colors hover:text-muted-foreground focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-60"
          >
            {enCours ? "Enregistrement…" : "Passer"}
          </button>
        </div>
      </div>
    )
  }

  /* Second temps : le ressenti posé, le coach répond. */
  const choix = RESSENTIS.find((r) => r.id === ressenti)!

  /* La ligne d'état du créneau, discrète, sous la coche. */
  let ligneStatut = "Séance vécue"
  if (jour !== null) {
    if (enCours) ligneStatut = "Enregistrement…"
    else if (erreur) ligneStatut = "Réponse non enregistrée"
    else ligneStatut = `Séance notée · ${JOURS[jour]}`
  }

  return (
    <div className="flex flex-1 flex-col justify-center py-12">
      <div className="flex flex-col items-center text-center">
        <span
          aria-hidden
          className="grid size-14 place-items-center rounded-full border border-border-strong bg-foreground/10 text-foreground"
        >
          <CheckIcon className="size-7" />
        </span>
        <p
          role="status"
          className="mt-5 font-heading text-xs tracking-brand text-faint uppercase"
        >
          {ligneStatut}
        </p>
      </div>

      {/* La réaction du coach — sa voix, en réponse à ce qu'elle vient de vivre. */}
      <div className="mt-8 rounded-lg border border-border bg-card px-6 py-6">
        <p className="text-[1.0625rem] leading-relaxed text-pretty">
          {choix.reaction}
        </p>
        <p className="mt-5 flex items-center gap-2.5 text-[0.875rem] text-faint">
          <span
            aria-hidden
            className="grid size-7 place-items-center rounded-full border border-border-strong bg-foreground/10 font-heading text-xs text-foreground"
          >
            {coach.charAt(0)}
          </span>
          {coach}, votre coach
        </p>
      </div>

      <p className="mt-8 text-center text-[0.9375rem] text-faint text-pretty">
        Cette séance rejoint votre parcours.
      </p>

      <div className="mx-auto mt-5 flex w-full max-w-[320px] flex-col gap-3">
        <Link
          href="/mon-espace/parcours"
          className="flex items-center justify-center rounded-lg border border-foreground bg-foreground px-5 py-3 font-heading text-[0.8125rem] tracking-brand uppercase text-background outline-none transition-colors hover:bg-foreground/90 focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          Voir mon parcours
        </Link>
        <Link
          href="/mon-espace"
          className="flex items-center justify-center rounded-lg border border-border px-5 py-3 font-heading text-[0.8125rem] tracking-brand uppercase text-muted-foreground outline-none transition-colors hover:border-border-strong hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          Revenir à l&apos;accueil
        </Link>

        {erreur ? (
          <button
            type="button"
            onClick={() => noter()}
            disabled={enCours}
            className="rounded-sm text-center text-[0.875rem] text-muted-foreground underline underline-offset-4 outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-60"
          >
            Réessayer d&apos;enregistrer la séance
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** « 4:30 » au-delà d'une minute, « 45s » en deçà — court, lisible d'un regard. */
function formatTemps(secondes: number): string {
  const m = Math.floor(secondes / 60)
  const s = secondes % 60
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`
}
